import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  BrandS,
  BrandXXS,
  LabelL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import type { SectionType } from '@sorare/core/src/atoms/ui/SectionsBar';
import { SectionsBar } from '@sorare/core/src/atoms/ui/SectionsBar';
import { Dialog } from '@sorare/core/src/components/dialog';
import { AnyRewardConfig } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import type {
  ThresholdsDuoTaskDialogLeg_user,
  ThresholdsDuoTaskDialog_taskInterface,
} from './__generated__/index.graphql';

const Content = styled(Vertical)`
  padding: var(--double-unit);
`;

const Label = styled(SBHorizontal)`
  align-self: flex-end;
  width: 100%;
`;

const BrandSup = styled(BrandXXS).attrs({ as: 'sup', bold: true })`
  vertical-align: top;
`;

const MAX_SCORE_SECTION = 440;

const Overview = styled(Vertical)`
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
  padding: var(--triple-unit);
`;

const Leg = ({
  score,
  target,
  user,
}: {
  score: number;
  target: number;
  user: ReactNode;
}) => {
  const sections: SectionType[] = [
    {
      width: `${(target / MAX_SCORE_SECTION) * 100}%`,
      content: (
        <Label color="var(--c-nd-600)" as={LabelS}>
          0
        </Label>
      ),
    },
    {
      content: (
        <Label bold as={LabelS} color="var(--c-score-high)">
          {target}
        </Label>
      ),
      background:
        score >= target
          ? 'rgba(var(--c-rgb-target), 0.5)'
          : 'rgba(var(--c-rgb-target), 0.25)',
    },
  ];

  return (
    <Vertical
      gap={2}
      style={
        {
          '--c-rgb-target': '0, 243, 235',
        } as CSSProperties
      }
    >
      <SBHorizontal>
        {user}
        <BrandS>
          <FormattedMessage
            {...fantasy.ordinalPoints}
            values={{
              score,
              sup: c => <BrandSup color="var(--c-nd-600)">{c}</BrandSup>,
            }}
          />
        </BrandS>
      </SBHorizontal>
      <SectionsBar
        sections={sections}
        cursorProps={{
          color: score >= target ? 'var(--c-score-high)' : 'var(--c-white)',
          rightPosition: `calc(100% - ${(Math.min(score, MAX_SCORE_SECTION) / MAX_SCORE_SECTION) * 100}%)`,
        }}
      />
    </Vertical>
  );
};

Leg.fragments = {
  user: gql`
    fragment ThresholdsDuoTaskDialogLeg_user on User {
      slug
      ...Avatar_user
      ...UserName_publicUserInfoInterface
    }
  ` as TypedDocumentNode<ThresholdsDuoTaskDialogLeg_user>,
};

export const ThresholdsDuoTaskDialog = ({
  task,
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
  task: ThresholdsDuoTaskDialog_taskInterface;
}) => {
  const { currentUser } = useCurrentUserContext();
  if (!isType(task, 'ThresholdsDuoConnectionTask') || !currentUser) {
    return null;
  }
  const { connectedUser, rewardConfigs, description, target, connectedLineup } =
    task;

  const myLineupScore = task.myLineup?.so5Rankings?.[0]?.score ?? 0;
  const connectedLineupScore = connectedLineup?.so5Rankings?.[0]?.score ?? 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        <LabelL className="text-center" bold>
          <FormattedMessage
            id="ThresholdsDuoTaskDialog.title"
            defaultMessage="Duo Challenge"
          />
        </LabelL>
      }
      maxWidth="xs"
    >
      <Content gap={5}>
        <Vertical gap={2}>
          <SBHorizontal>
            <Horizontal>
              <Avatar user={currentUser} rounded size={4} />
              <Vertical gap={0.5}>
                <Horizontal as={LabelS} gap={0.5} color="var(--c-yellow-400)">
                  <FontAwesomeIcon icon={faClock} />
                  {!!task.campaign?.endDate && (
                    <TimeLeft time={task.campaign?.endDate} />
                  )}
                </Horizontal>
                <LabelS bold>
                  <FormattedMessage {...glossary.you} />
                </LabelS>
              </Vertical>
            </Horizontal>
            <Vertical gap={0.5} style={{ alignItems: 'flex-end' }}>
              <LabelS color="var(--c-nd-600)">
                <FormattedMessage
                  id="ThresholdsDuoTaskDialog.duoPartner"
                  defaultMessage="Duo partner"
                />
              </LabelS>
              <Horizontal gap={0.5}>
                <Avatar user={connectedUser} rounded size={2} />
                <LabelS bold>
                  <UserName user={connectedUser} />
                </LabelS>
              </Horizontal>
            </Vertical>
          </SBHorizontal>
          <Overview gap={3}>
            <Leg
              score={myLineupScore}
              target={target}
              user={
                <Horizontal>
                  <Avatar user={currentUser} rounded size={2} />
                  <LabelL bold>
                    <FormattedMessage {...glossary.you} />
                  </LabelL>
                </Horizontal>
              }
            />
            <Leg
              score={connectedLineupScore}
              target={target}
              user={
                <Horizontal>
                  <Avatar user={connectedUser} rounded size={2} />
                  <LabelL bold>
                    <UserName user={connectedUser} />
                  </LabelL>
                </Horizontal>
              }
            />
            {!!description && (
              <BodyM className="text-center">{description}</BodyM>
            )}
          </Overview>
        </Vertical>
        <SBHorizontal>
          <LabelL bold>
            <FormattedMessage
              id="ThresholdsDuoTaskDialog.teamRewards"
              defaultMessage="Team rewards"
            />
          </LabelL>
          <Horizontal>
            {rewardConfigs.map(rewardConfig => (
              <AnyRewardConfig
                key={rewardConfig.id}
                anyRewardConfig={rewardConfig}
              />
            ))}
          </Horizontal>
        </SBHorizontal>
        <BodyS color="var(--c-nd-600)" className="text-center">
          <FormattedMessage
            id="ThresholdsDuoTaskDialog.mention"
            defaultMessage="You and your friend must complete the mission simultaneously. Scores reset each GameWeek."
          />
        </BodyS>
      </Content>
    </Dialog>
  );
};

ThresholdsDuoTaskDialog.fragments = {
  taskInterface: gql`
    fragment ThresholdsDuoTaskDialog_taskInterface on TaskInterface {
      id
      description
      target
      aasmState
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
      ... on ThresholdsDuoConnectionTask {
        id
        campaign {
          slug
          endDate
        }
        myLineup {
          id
          so5Rankings {
            id
            score
          }
        }
        connectedLineup {
          id
          so5Rankings {
            id
            score
          }
        }
        connectedUser {
          slug
          ...Avatar_user
          ...UserName_publicUserInfoInterface
        }
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<ThresholdsDuoTaskDialog_taskInterface>,
};
